import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Send, Square } from 'lucide-react';

/**
 * InterviewControls — single-line footer: mic toggle, text input, send —
 * all in one row (mic click-to-toggle streaming recording; typing is the
 * fallback). Recording state shows as a red pulsing mic + inline timer
 * instead of a separate stacked status line, to keep this compact.
 */
export default function InterviewControls({ onSubmitText, onStartRecording, onStopRecording, recording, disabled, micOn }) {
  const [textInput, setTextInput] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const handleTextSubmit = useCallback(() => {
    if (!textInput.trim() || disabled) return;
    onSubmitText(textInput.trim());
    setTextInput('');
  }, [textInput, disabled, onSubmitText]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  const handleMicClick = useCallback(() => {
    if (!micOn || disabled) return;
    if (recording) {
      onStopRecording?.();
    } else {
      onStartRecording?.();
    }
  }, [recording, micOn, disabled, onStartRecording, onStopRecording]);

  // Elapsed timer while recording
  useEffect(() => {
    if (recording) {
      startTimeRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } else {
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [recording]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2 w-full max-w-3xl mx-auto" data-testid="interview-controls">
      {/* Mic toggle */}
      <button
        onClick={handleMicClick}
        disabled={disabled || !micOn}
        data-testid="mic-toggle-btn"
        title={recording ? 'Click to stop & send' : disabled ? 'Waiting for AI...' : 'Click to record'}
        className={`relative shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all
          ${recording
            ? 'bg-red-500/20 ring-2 ring-red-500'
            : disabled
              ? 'bg-[#1C1F26] opacity-50 cursor-not-allowed'
              : 'bg-[#1C1F26] hover:bg-[#272B36] active:scale-95'
          }`}
        style={!recording && !disabled ? {
          boxShadow: '0 0 0 3px rgba(255, 194, 51, 0.15)',
        } : undefined}
      >
        {recording
          ? <Square className="w-3.5 h-3.5 text-red-400 fill-red-400" />
          : <Mic className="w-4 h-4 text-[#FFC233]" />
        }
        {recording && (
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        )}
      </button>

      {recording && (
        <span className="shrink-0 text-xs font-mono text-red-400 font-medium tabular-nums w-9">
          {formatTime(elapsed)}
        </span>
      )}

      {/* Text input */}
      <input
        type="text"
        value={textInput}
        onChange={(e) => setTextInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={recording ? 'Listening...' : disabled ? 'Waiting for AI...' : 'Type your answer...'}
        disabled={disabled}
        data-testid="text-input-fallback"
        className="flex-1 min-w-0 px-4 py-2.5 rounded-xl bg-[#14161B] border border-[#272B36] text-sm text-white
                   placeholder-[#4B5063] focus:outline-none focus:border-[#3D1F6E] transition-colors
                   disabled:opacity-50"
      />

      {/* Send */}
      <button
        onClick={handleTextSubmit}
        disabled={disabled || !textInput.trim()}
        data-testid="send-text-btn"
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center
                   bg-[#3D1F6E] text-white hover:bg-[#5B2D9E] transition-colors
                   disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
}
